/* Line diff for policy review (design eval F18): a plain LCS over lines.
   Policies are short, so the O(n*m) table is fine; past MAX_CELLS it falls
   back to "every old line removed, every new line added" rather than hang. */

export type DiffOp = 'same' | 'add' | 'del';

export interface DiffLine {
    op: DiffOp;
    text: string;
    /** 1-based line in the old text (same, del). */
    oldLine?: number;
    /** 1-based line in the new text (same, add). */
    newLine?: number;
}

const MAX_CELLS = 4_000_000;

export function splitLines(text: string): string[] {
    if (text === '') return [];
    const lines = text.replace(/\r\n?/g, '\n').split('\n');
    // A trailing newline is not an extra empty line.
    if (lines[lines.length - 1] === '') lines.pop();
    return lines;
}

export function lineDiff(oldText: string, newText: string): DiffLine[] {
    const a = splitLines(oldText);
    const b = splitLines(newText);

    // Trim the common prefix and suffix so the table only covers the change.
    let start = 0;
    while (start < a.length && start < b.length && a[start] === b[start]) start++;
    let endA = a.length;
    let endB = b.length;
    while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) { endA--; endB--; }

    const out: DiffLine[] = [];
    for (let i = 0; i < start; i++) out.push({ op: 'same', text: a[i], oldLine: i + 1, newLine: i + 1 });

    const n = endA - start;
    const m = endB - start;
    if (n * m > MAX_CELLS) {
        for (let i = start; i < endA; i++) out.push({ op: 'del', text: a[i], oldLine: i + 1 });
        for (let j = start; j < endB; j++) out.push({ op: 'add', text: b[j], newLine: j + 1 });
    } else {
        // lcs[i][j] = LCS length of a[start+i..endA) and b[start+j..endB)
        const w = m + 1;
        const lcs = new Uint32Array((n + 1) * w);
        for (let i = n - 1; i >= 0; i--) {
            for (let j = m - 1; j >= 0; j--) {
                lcs[i * w + j] = a[start + i] === b[start + j]
                    ? lcs[(i + 1) * w + j + 1] + 1
                    : Math.max(lcs[(i + 1) * w + j], lcs[i * w + j + 1]);
            }
        }
        let i = 0;
        let j = 0;
        while (i < n || j < m) {
            if (i < n && j < m && a[start + i] === b[start + j]) {
                out.push({ op: 'same', text: a[start + i], oldLine: start + i + 1, newLine: start + j + 1 });
                i++; j++;
            } else if (i < n && (j === m || lcs[(i + 1) * w + j] >= lcs[i * w + j + 1])) {
                // Removals before additions, as unified diffs read.
                out.push({ op: 'del', text: a[start + i], oldLine: start + i + 1 });
                i++;
            } else {
                out.push({ op: 'add', text: b[start + j], newLine: start + j + 1 });
                j++;
            }
        }
    }

    for (let k = 0; k < a.length - endA; k++) {
        out.push({ op: 'same', text: a[endA + k], oldLine: endA + k + 1, newLine: endB + k + 1 });
    }
    return out;
}

export function diffStats(diff: DiffLine[]): { added: number; removed: number } {
    let added = 0;
    let removed = 0;
    for (const d of diff) {
        if (d.op === 'add') added++;
        else if (d.op === 'del') removed++;
    }
    return { added, removed };
}

/** Collapse long runs of unchanged lines to `context` lines either side of a
    change. A `{ hidden }` row stands for a collapsed run. */
export function withContext(diff: DiffLine[], context = 3): (DiffLine | { hidden: number })[] {
    const keep = new Array(diff.length).fill(false);
    diff.forEach((d, idx) => {
        if (d.op === 'same') return;
        for (let k = Math.max(0, idx - context); k <= Math.min(diff.length - 1, idx + context); k++) keep[k] = true;
    });
    const rows: (DiffLine | { hidden: number })[] = [];
    let hidden = 0;
    diff.forEach((d, idx) => {
        if (keep[idx]) {
            if (hidden) { rows.push({ hidden }); hidden = 0; }
            rows.push(d);
        } else {
            hidden++;
        }
    });
    if (hidden) rows.push({ hidden });
    return rows;
}
